/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2024
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import StringUtils from './StringUtils';
import BaseMoserObject from './BaseMoserObject';
import V5QueryExpressionParserTokenValidator from './V5QueryExpressionParserTokenValidator';
import IdentifierUtil from './IdentifierUtil';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/common/utils/V5QueryExpressionParserTokenValidator (static) // isLetterOrDigit, isLetter
//@import com/ibm/bi/platform/moser/common/utils/IdentifierUtil (static) // isV5Aggregate, isKeyword, isHyphenatedKeyword

/**
 * item scanner.
 * @author  hex
 */
export default class ItemScanner extends BaseMoserObject{

	/**
	 * collect V5functions from expression.
	 * @param {String} expr  expression.
	 * @return {boolean}  V5functions.
	 */
	static hasV5AggregateFunctions(expr) {
		if (expr == null || expr.length === 0) {
			return false;
		}
		var length = expr.length;
		var pos = 0;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			var p = ItemScanner._skipMacroStringPromptComment(expr, aChar, pos);
			if (p != -1) {
				pos = p;
			} else if (V5QueryExpressionParserTokenValidator.isLetter(aChar) || aChar == 95) {
				var bAggregate = [false];
				var hNextPos = ItemScanner._skipV5Function(expr, pos, bAggregate);
				if (hNextPos > -1) {
					pos = hNextPos;
					if (bAggregate[0]) {
						return true;
					}
				} else {
					var bvalid = [true];
					var parts = [];
					var nextpos = ItemScanner._skipIdentifier(expr, pos + 1, bvalid, parts);
					pos = nextpos;
				}
			} else if (aChar == 91) {
				var bvalid = [true];
				var parts = [];
				var nextpos = ItemScanner._skipBracketName(expr, pos + 1, bvalid, parts);
				pos = nextpos;
			} else {
				++pos;
			}
		}
		return false;
	}

	/**
	 * skip macro, string, prompt, or comment
	 * @param {String} expr
	 * @param {int} aChar
	 * @param {int} pos
	 * @return {int}
	 */
	static _skipMacroStringPromptComment(expr, aChar, pos) {
		if (aChar == 35) {
			return ItemScanner._skipMacro(expr, pos + 1);
		}
		if (aChar == 39) {
			return ItemScanner._skipSingleQuoteString(expr, pos + 1);
		}
		if (aChar == 34) {
			return ItemScanner._skipDoubleQuoteString(expr, pos + 1);
		}
		if (aChar == 63) {
			return ItemScanner._skipPrompt(expr, pos + 1);
		}
		if (ItemScanner._isComment(expr, pos)) {
			return ItemScanner._skipComment(expr, pos + 2);
		}
		if (ItemScanner._isMultiLineComment(expr, pos)) {
			return ItemScanner._skipMultiLineComment(expr, pos + 2);
		}
		return -1;
	}

	/**
	 * collect identifiers from expression.
	 * @param {String} expr  expression.
	 * @return {Array}  identifiers.
	 */
	static collectIdentifiers(expr) {
		var rtLst = [];
		if (expr == null || expr.length === 0) {
			return rtLst;
		}
		var length = expr.length;
		var pos = 0;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			var p = ItemScanner._skipMacroStringPromptComment(expr, aChar, pos);
			if (p != -1) {
				pos = p;
			} else if (V5QueryExpressionParserTokenValidator.isLetter(aChar) || aChar == 95) {
				var hNextPos = ItemScanner._skipHyphenatedKeyword(expr, pos);
				if (hNextPos > -1) {
					pos = hNextPos;
				} else {
					var bvalid = [true];
					var parts = [];
					var nextpos = ItemScanner._skipIdentifier(expr, pos + 1, bvalid, parts);
					if (bvalid[0] && !(parts.length==0) && ItemScanner._isValidId(parts)) {
						rtLst.push(ItemScanner._generateParts(parts));
					}
					pos = nextpos;
				}
			} else if (aChar == 91) {
				var bvalid = [true];
				var parts = [];
				var nextpos = ItemScanner._skipBracketName(expr, pos + 1, bvalid, parts);
				if (bvalid[0] && !(parts.length==0)) {
					rtLst.push(ItemScanner._generateParts(parts));
				}
				pos = nextpos;
			} else {
				++pos;
			}
		}
		return rtLst;
	}

	/**
	 * @param {String} expr
	 * @param {int} st
	 * @param  rtLst
	 * @return {int}
	 */
	static _skipV5Function(expr, st, bAggregate) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var c = expr.codePointAt(pos);
			if (V5QueryExpressionParserTokenValidator.isLetterOrDigit(c) || c == 95 || c == 45) {
				++pos;
			} else {
				break;
			}
		}
		if (pos > st) {
			var nextPosSkipWhiteSpace = ItemScanner._skipWhiteSpace(expr, pos);
			if (nextPosSkipWhiteSpace >= length) {
				return -1;
			}
			var nextChar = expr.codePointAt(nextPosSkipWhiteSpace);
			if (nextChar != 40) {
				return -1;
			}
			var part = expr.substring(st, pos);
			part = part.trim();
			if (IdentifierUtil.isKeyword(part)) {
				bAggregate[0] = IdentifierUtil.isV5Aggregate(part);
				return pos;
			}
		}
		return -1;
	}

	/**
	 * Returns the position after the hyphenated word or -1 if the word does not contain a hyphen.
	 * @param {String} expr
	 * @param {int} st
	 * @return {int}
	 */
	static _skipHyphenatedKeyword(expr, st) {
		var length = expr.length;
		var hasHyphen = false;
		var pos = st;
		while (pos < length) {
			var c = expr.codePointAt(pos);
			if (V5QueryExpressionParserTokenValidator.isLetterOrDigit(c) || c == 95) {
				++pos;
			} else if (c == 45) {
				++pos;
				hasHyphen = true;
			} else {
				break;
			}
		}
		if (hasHyphen) {
			var part = expr.substring(st, pos);
			if (IdentifierUtil.isHyphenatedKeyword(part)) {
				return pos;
			}
		}
		return -1;
	}

	static _isValidId(parts) {
		return parts.length != 1 || !(IdentifierUtil.isKeyword(parts[0]));
	}

	static _generateParts(parts) {
		var p = [];
		for (var i = 0; i < parts.length; ++i) {
			p[i] = parts[i];
		}
		return p;
	}

	/**
	 * generate identifier.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @param {int} end  end point.
	 * @return {String}  identifier.
	 */
	static _generatePart(expr, st, end) {
		var sb = "";
		var length = expr.length;
		for (var i = st; i < end && i < length; ++i) {
			var c = expr.codePointAt(i);
			sb+=StringUtils.fromCodePoint(c);
		}
		return sb;
	}

	/**
	 * Processes a multi-part identifier e.g a.  b. c in a recursive fashion.
	 * @param {String} expr 	expression.
	 * @param {int} st  start point.
	 * @param {boolean[]} bvalid  valid?
	 * @param {Array} parts  parts.
	 * @return {int}  next position.
	 */
	static _skipIdentifier(expr, st, bvalid, parts) {
		var nextPos = ItemScanner._skipPart(expr, st);
		var nextPosSkipWhiteSpace = ItemScanner._skipWhiteSpace(expr, nextPos);
		var length = expr.length;
		if (nextPosSkipWhiteSpace >= length) {
			bvalid[0] = true;
			parts.push(ItemScanner._generatePart(expr, st - 1, nextPos));
			return nextPosSkipWhiteSpace;
		}
		var nextChar = expr.codePointAt(nextPosSkipWhiteSpace);
		if (nextChar == 40) {
			bvalid[0] = false;
			return nextPosSkipWhiteSpace;
		}
		if (nextChar != 46) {
			bvalid[0] = true;
			parts.push(ItemScanner._generatePart(expr, st - 1, nextPos));
			return nextPosSkipWhiteSpace;
		}
		var posAfterDot = ItemScanner._skipWhiteSpace(expr, nextPosSkipWhiteSpace + 1);
		if (posAfterDot >= length) {
			bvalid[0] = false;
			return posAfterDot;
		}
		var nextnextChar = expr.codePointAt(posAfterDot);
		if (!(V5QueryExpressionParserTokenValidator.isLetter(nextnextChar)) && nextnextChar != 95) {
			bvalid[0] = false;
			return posAfterDot;
		}
		parts.push(ItemScanner._generatePart(expr, st - 1, nextPos));
		return ItemScanner._skipIdentifier(expr, posAfterDot + 1, bvalid, parts);
	}

	/**
	 * V5 ?
	 * @param {String} expr 	expression.
	 * @param {int} st  start point.
	 * @param {boolean[]} bvalid  valid?
	 * @param {Array} parts  parts.
	 * @return {int}  next position.
	 */
	static _skipBracketName(expr, st, bvalid, parts) {
		var nextPos = ItemScanner._skipBracketPart(expr, st);
		var nextPosSkipWhiteSpec = ItemScanner._skipWhiteSpace(expr, nextPos);
		var length = expr.length;
		if (nextPosSkipWhiteSpec >= length) {
			bvalid[0] = true;
			parts.push(ItemScanner._generatePart(expr, st - 1, nextPos));
			return nextPosSkipWhiteSpec;
		}
		var nextChar = expr.codePointAt(nextPosSkipWhiteSpec);
		if (nextChar == 40) {
			bvalid[0] = false;
			return nextPosSkipWhiteSpec;
		}
		if (nextChar != 46) {
			bvalid[0] = true;
			parts.push(ItemScanner._generatePart(expr, st - 1, nextPos));
			return nextPosSkipWhiteSpec;
		}
		var posAfterDot = ItemScanner._skipWhiteSpace(expr, nextPosSkipWhiteSpec + 1);
		if (posAfterDot >= length) {
			bvalid[0] = false;
			return posAfterDot;
		}
		var nextnextChar = expr.codePointAt(posAfterDot);
		if (nextnextChar != 91) {
			bvalid[0] = false;
			return posAfterDot;
		}
		parts.push(ItemScanner._generatePart(expr, st - 1, nextPos));
		return ItemScanner._skipBracketName(expr, posAfterDot + 1, bvalid, parts);
	}

	static _skipWhiteSpace(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			if (ItemScanner._isWhiteSpace(aChar)) {
				++pos;
			} else {
				break;
			}
		}
		return pos;
	}

	/**
	 * skip part.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {int}  next position.
	 */
	static _skipPart(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var c = expr.codePointAt(pos);
			if (V5QueryExpressionParserTokenValidator.isLetterOrDigit(c) || c == 95) {
				++pos;
			} else {
				break;
			}
		}
		return pos;
	}

	/**
	 * skip bracket part.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {int}  next position.
	 */
	static _skipBracketPart(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			if (aChar == 93) {
				if (pos == length - 1) {
					return length;
				}
				var nextPos = pos + 1;
				if (nextPos < length && expr.codePointAt(nextPos) == 93) {
					pos = nextPos + 1;
					continue;
				}
				return nextPos;
			}
			++pos;
		}
		return pos;
	}

	/**
	 * white space?
	 * @param {int} c  char.
	 * @return {boolean}  t/f.
	 */
	static _isWhiteSpace(c) {
		return c == 32 || c == 9 || c == 10 || c == 13;
	}

	/**
	 * skip macro block.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {int}  position after macro.
	 */
	static _skipMacro(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			if (aChar == 35) {
				return ++pos;
			}
			if (aChar == 39) {
				pos = ItemScanner._skipSingleQuoteString(expr, pos + 1);
			} else if (aChar == 34) {
				pos = ItemScanner._skipDoubleQuoteString(expr, pos + 1);
			} else if (aChar == 63) {
				pos = ItemScanner._skipPrompt(expr, pos + 1);
			} else if (ItemScanner._isComment(expr, pos)) {
				pos = ItemScanner._skipComment(expr, pos + 2);
			} else if (ItemScanner._isMultiLineComment(expr, pos)) {
				pos = ItemScanner._skipMultiLineComment(expr, pos + 2);
			} else {
				++pos;
			}
		}
		return pos;
	}

	/**
	 * is comment?
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {boolean}  t/f.
	 */
	static _isComment(expr, st) {
		var length = expr.length;
		if (st + 1 < length) {
			var firstChar = expr.codePointAt(st);
			var secondChar = expr.codePointAt(st + 1);
			return firstChar == 47 && secondChar == 47 || firstChar == 45 && secondChar == 45;
		}
		return false;
	}

	/**
	 * is multi line comment?
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {boolean}  t/f.
	 */
	static _isMultiLineComment(expr, st) {
		var length = expr.length;
		if (st + 1 < length) {
			var firstChar = expr.codePointAt(st);
			var secondChar = expr.codePointAt(st + 1);
			return firstChar == 47 && secondChar == 42;
		}
		return false;
	}

	/**
	 * skip single quote string.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {int}  position after single quote string.
	 */
	static _skipSingleQuoteString(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			if (aChar == 39) {
				if (pos == length - 1) {
					return length;
				}
				var nextPos = pos + 1;
				if (nextPos < length && expr.codePointAt(nextPos) == 39) {
					pos = nextPos + 1;
					continue;
				}
				return nextPos;
			}
			++pos;
		}
		return pos;
	}

	/**
	 * skip double quote string.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {int}  position after double quote string.
	 */
	static _skipDoubleQuoteString(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			if (aChar == 34) {
				if (pos == length - 1) {
					return length;
				}
				var nextPos = pos + 1;
				if (nextPos < length && expr.codePointAt(nextPos) == 34) {
					pos = nextPos + 1;
					continue;
				}
				return nextPos;
			}
			++pos;
		}
		return pos;
	}

	/**
	 * skip prompt.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {int}  position after prompt.
	 */
	static _skipPrompt(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			if (aChar == 63) {
				return ++pos;
			}
			++pos;
		}
		return pos;
	}

	/**
	 * skip comment.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {int}  position after comment.
	 */
	static _skipComment(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			if (aChar == 10 || aChar == 13) {
				return ++pos;
			}
			++pos;
		}
		return pos;
	}

	/**
	 * skip multi line comment.
	 * @param {String} expr  expression.
	 * @param {int} st  start point.
	 * @return {int}  position after multi line comment.
	 */
	static _skipMultiLineComment(expr, st) {
		var length = expr.length;
		var pos = st;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			if (aChar == 42) {
				var nextPos = pos + 1;
				if (nextPos < length && expr.codePointAt(nextPos) == 47) {
					return ++nextPos;
				}
			}
			++pos;
		}
		return pos;
	}

	static replaceIdForExpr(targetObjType, expr, targetIdforExpr, srcObjType, oldSrcId, newSrcId) {
		if (expr == null || expr.length === 0) {
			return null;
		}
		var srcParts = oldSrcId.split(new RegExp("\\."));
		return ItemScanner._replaceExpressionString(true, srcParts, targetObjType, expr, targetIdforExpr, srcObjType, newSrcId);
	}

	/**
	 * for both replaceIdForExpr and replaceTerm to avoid duplicate
	 * @param {boolean} bIdForExpr
	 * @param {String[]} srcParts
	 * @param {String} targetObjType
	 * @param {String} expr
	 * @param {String} targetIdforExpr
	 * @param {String} srcObjType
	 * @param  oldSrcId
	 * @param {String} newSrcId
	 * @return {String}
	 */
	static _replaceExpressionString(bIdForExpr, srcParts, targetObjType, expr, targetIdforExpr, srcObjType, newSrcId) {
		var bChanged = false;
		var sb = "";
		var length = expr.length;
		var pos = 0;
		while (pos < length) {
			var aChar = expr.codePointAt(pos);
			var oldPos = pos;
			if (aChar == 35) {
				pos = ItemScanner._skipMacro(expr, pos + 1);
				if (!bIdForExpr && "macro" == targetObjType) {
					sb+=(newSrcId);
					bChanged = true;
				} else {
					sb+=(ItemScanner._generatePart(expr, oldPos, pos));
				}
			} else if (aChar == 39) {
				pos = ItemScanner._skipSingleQuoteString(expr, pos + 1);
				sb+=(ItemScanner._generatePart(expr, oldPos, pos));
			} else if (aChar == 34) {
				pos = ItemScanner._skipDoubleQuoteString(expr, pos + 1);
				sb+=(ItemScanner._generatePart(expr, oldPos, pos));
			} else if (aChar == 63) {
				pos = ItemScanner._skipPrompt(expr, pos + 1);
				sb+=(ItemScanner._generatePart(expr, oldPos, pos));
			} else if (ItemScanner._isComment(expr, pos)) {
				pos = ItemScanner._skipComment(expr, pos + 2);
				sb+=(ItemScanner._generatePart(expr, oldPos, pos));
			} else if (ItemScanner._isMultiLineComment(expr, pos)) {
				pos = ItemScanner._skipMultiLineComment(expr, pos + 2);
				sb+=(ItemScanner._generatePart(expr, oldPos, pos));
			} else if (V5QueryExpressionParserTokenValidator.isLetter(aChar) || aChar == 95) {
				var bvalid = [true];
				var parts = [];
				var nextpos = ItemScanner._skipIdentifier(expr, pos + 1, bvalid, parts);
				var toAppend = ItemScanner._getStringToAppend(false, bvalid, parts, targetObjType, srcParts, srcObjType, newSrcId, targetIdforExpr, bIdForExpr, expr, oldPos, nextpos);
				sb+=(toAppend);
				pos = nextpos;
				if (bvalid[0]) {
					bChanged = true;
				}
			} else if (aChar == 91) {
				var bvalid = [true];
				var parts = [];
				var nextpos = ItemScanner._skipBracketName(expr, pos + 1, bvalid, parts);
				var toAppend = ItemScanner._getStringToAppend(true, bvalid, parts, targetObjType, srcParts, srcObjType, newSrcId, targetIdforExpr, bIdForExpr, expr, oldPos, nextpos);
				sb+=(toAppend);
				pos = nextpos;
				if (bvalid[0]) {
					bChanged = true;
				}
			} else {
				++pos;
				sb+=(ItemScanner._generatePart(expr, oldPos, pos));
			}
		}
		if (!bChanged && bIdForExpr) {
			return null;
		}
		return sb;
	}

	/**
	 * for both identifier and V5UniqueName to avoid duplicate
	 * @param {boolean} bV5Id
	 * @param {boolean[]} bvalid
	 * @param {Array} parts
	 * @param {String} targetObjType
	 * @param {String[]} srcParts
	 * @param {String} srcObjType
	 * @param {String} newSrcId
	 * @param {String} targetIdforExpr
	 * @param {boolean} bIdForExpr
	 * @param {String} expr
	 * @param {int} oldPos
	 * @param {int} nextpos
	 * @return {String}
	 */
	static _getStringToAppend(bV5Id, bvalid, parts, targetObjType, srcParts, srcObjType, newSrcId, targetIdforExpr, bIdForExpr, expr, oldPos, nextpos) {
		var sb = "";
		var bOK = bvalid[0] && !(parts.length==0);
		if (bOK && !bV5Id) {
			bOK = ItemScanner._isValidId(parts);
		}
		var b = false;
		if (bOK) {
			var newPart = null;
			if (bIdForExpr) {
				newPart = ItemScanner._matchOp(parts, targetObjType, srcParts, srcObjType, newSrcId, bV5Id, targetIdforExpr);
			} else if (("identifier" == targetObjType || "V5UniqueName" == targetObjType) && ItemScanner._compareArrayAndList(srcParts, parts)) {
				newPart = newSrcId;
			}
			if (newPart != null) {
				sb+=(newPart);
				b = true;
				var st = ItemScanner._getWhiteSpace(expr, nextpos - 1);
				if (st >= 0) {
					sb+=(ItemScanner._generatePart(expr, st, nextpos));
				}
			}
		}
		bvalid[0] = b;
		if (!b) {
			return ItemScanner._generatePart(expr, oldPos, nextpos);
		}
		return sb;
	}

	static _getWhiteSpace(expr, lastPos) {
		var st = -1;
		for (var i = lastPos; i >= 0; --i) {
			var aChar = expr.codePointAt(i);
			if (!(ItemScanner._isWhiteSpace(aChar))) {
				break;
			}
			st = i;
		}
		return st;
	}

	static _matchOp(parts, targetObjType, srcParts, srcObjType, newSrcId, bBracket, targetIdforExpr) {
		if ("Calculation" == srcObjType) {
			if (!("Calculation" == targetObjType) && !("Filter" == targetObjType)) {
				return null;
			}
			return ItemScanner._matchGlobalCalcFilter(srcParts, parts, newSrcId, bBracket);
		}
		if ("QuerySubject" == srcObjType) {
			if (!("Calculation" == targetObjType) && !("Filter" == targetObjType) && !("QueryItem" == targetObjType)) {
				return null;
			}
			return ItemScanner._matchQuerySubject(srcParts, parts, newSrcId, bBracket);
		}
		if ("QueryItem" == srcObjType) {
			if (!("Calculation" == targetObjType) && !("Filter" == targetObjType) && !("QueryItem" == targetObjType)) {
				return null;
			}
			return ItemScanner._matchQueryItem(srcParts, parts, newSrcId, bBracket, targetObjType, targetIdforExpr);
		}
		return null;
	}

	static _matchGlobalCalcFilter(srcParts, targetParts, newSrcId, bBracket) {
		if (srcParts.length != 1 || targetParts.length != 1) {
			return null;
		}
		var target = targetParts[0];
		if (bBracket) {
			target = target.substring(1, target.length - 1);
		}
		if (srcParts[0] == target) {
			if (bBracket) {
				return "[" + newSrcId + "]";
			}
			return newSrcId;
		}
		return null;
	}

	static _matchQuerySubject(srcParts, targetParts, newSrcId, bBracket) {
		if (srcParts.length != 1 || targetParts.length==0) {
			return null;
		}
		if (targetParts.length == 1) {
			var target = targetParts[0];
			if (bBracket) {
				target = target.substring(1, target.length - 1);
				var parts = target.split(new RegExp("\\."));
				if (parts.length > 1 && srcParts[0] == parts[0]) {
					parts[0] = newSrcId;
					return "[" + ItemScanner._generateIdFromParts(parts, 0) + "]";
				}
			}
		} else {
			var parts = ItemScanner._generateParts(targetParts);
			if (parts.length > 1) {
				if (bBracket) {
					var p = "[" + srcParts[0] + "]";
					if (p == parts[0]) {
						parts[0] = "[" + newSrcId + "]";
						return ItemScanner._generateIdFromParts(parts, 0);
					}
				} else {
					if (srcParts[0] == parts[0]) {
						parts[0] = newSrcId;
						return ItemScanner._generateIdFromParts(parts, 0);
					}
				}
			}
		}
		return null;
	}

	static _matchQueryItem(srcParts, targetParts, newSrcId, bBracket, targetObjType, targetIdforExpr) {
		if (srcParts.length < 2) {
			return null;
		}
		if ("Calculation" == targetObjType || "Filter" == targetObjType) {
			return ItemScanner._matchQueryItemByIdForExpr(srcParts, targetParts, newSrcId, bBracket);
		}
		if ("QueryItem" == targetObjType) {
			var targetQIParts = targetIdforExpr.split(new RegExp("\\."));
			if (!(srcParts[0] == targetQIParts[0])) {
				return ItemScanner._matchQueryItemByIdForExpr(srcParts, targetParts, newSrcId, bBracket);
			}
			var tParts = ItemScanner._generateParts(targetParts);
			var target = tParts[0];
			if (bBracket) {
				target = target.substring(1, target.length - 1);
			}
			if (!(srcParts[0] == target) && ItemScanner._partsEquals(srcParts, tParts, bBracket, 1)) {
				var parts = newSrcId.split(new RegExp("\\."));
				if (bBracket) {
					for (var i = 0; i < parts.length; ++i) {
						parts[i] = "[" + parts[i] + "]";
					}
				}
				return ItemScanner._generateIdFromParts(parts, 1);
			}
			return ItemScanner._matchQueryItemByIdForExpr(srcParts, targetParts, newSrcId, bBracket);
		}
		return null;
	}

	static _matchQueryItemByIdForExpr(srcParts, targetParts, newSrcId, bBracket) {
		if (targetParts.length == 1) {
			if (bBracket) {
				var target = targetParts[0];
				target = target.substring(1, target.length - 1);
				var parts = target.split(new RegExp("\\."));
				if (ItemScanner._partsEquals(srcParts, parts, false, 0)) {
					return "[" + newSrcId + "]";
				}
			}
		} else {
			var tParts = ItemScanner._generateParts(targetParts);
			if (ItemScanner._partsEquals(srcParts, tParts, bBracket, 0)) {
				if (bBracket) {
					var parts = newSrcId.split(new RegExp("\\."));
					for (var i = 0; i < parts.length; ++i) {
						parts[i] = "[" + parts[i] + "]";
					}
					return ItemScanner._generateIdFromParts(parts, 0);
				}
				return newSrcId;
			}
		}
		return null;
	}

	static _generateIdFromParts(parts, st) {
		var sb = "";
		for (var i = st; i < parts.length; ++i) {
			if (i != st) {
				sb+=(".");
			}
			sb+=(parts[i]);
		}
		return sb;
	}

	static _partsEquals(srcParts, parts, bBracket, st) {
		var srcLength = srcParts.length - st;
		if (srcLength <= 0 || srcLength != parts.length) {
			return false;
		}
		for (var i = st, j = 0; i < srcParts.length; i++, j++) {
			var target = parts[j];
			if (bBracket) {
				target = target.substring(1, target.length - 1);
			}
			if (!(srcParts[i] == target)) {
				return false;
			}
		}
		return true;
	}

	static replaceTerm(expr, type, oldParts, newValue) {
		if (expr == null || expr.length === 0) {
			return expr;
		}
		return ItemScanner._replaceExpressionString(false, oldParts, type, expr, null, null, newValue);
	}

	static _compareArrayAndList(srcParts, parts) {
		var srcLength = srcParts.length;
		if (srcLength != parts.length) {
			return false;
		}
		for (var i = 0; i < srcLength; ++i) {
			if (!(srcParts[i] == parts[i])) {
				return false;
			}
		}
		return true;
	}

	/**
	 * Constructor.
	 */
	//constructor() 
}


// WEBPACK FOOTER //
// ./src/generated/ItemScanner.js